import { Component } from "@theme/component";

const CARD_SELECTOR = "product-card, .product-card, .resource-card";
const QUICK_ADD_SELECTOR = "quick-add-component, quick-add-dialog";
const FALLBACK = Object.freeze({ kind: "fallback" });
const NATIVE = Object.freeze({ kind: "native" });

/**
 * Read only inert, product-matched JSON from the PDP. Unknown app shapes must
 * never advertise a different tier or borrow a different product's discount.
 * @param {Document} document
 * @param {string} currency
 * @param {string} productId
 * @returns {{kind: string, cents?: number, currency?: string}}
 */
export function readEdenDeal(document, currency, productId) {
  try {
    const config = JSON.parse(
      document.querySelector(
        'script#kaching-bundles-config[type="application/json"]',
      )?.textContent || "null",
    );
    if (!config || String(config.productId) !== productId) return FALLBACK;
    if (config.marketCurrencyCode !== currency) return FALLBACK;
    if (config.abTestsRunning) return FALLBACK;

    const scripts = document.querySelectorAll(
      `script.kaching-bundles-deal-block-settings[type="application/json"][data-product-id="${productId}"]`,
    );
    if (!scripts.length) return NATIVE;

    const deals = [...scripts].map((script) =>
      JSON.parse(script.textContent || "null"),
    );
    const deal = deals[0];
    if (
      !deal ||
      deals.some((other) => JSON.stringify(other) !== JSON.stringify(deal))
    )
      return FALLBACK;
    if (!Array.isArray(deal.dealBars)) return FALLBACK;
    if (deal.priceRounding) return FALLBACK;
    if (deal.excludeB2bCustomers && config.b2bCustomer) return NATIVE;
    if (config.requireCustomerLogin && !config.isLoggedIn) return NATIVE;
    if (deal.excludedProductIds?.some((id) => String(id) === productId))
      return NATIVE;
    if (
      deal.excludedMarketIds?.some(
        (id) => String(id) === String(config.marketId),
      )
    )
      return NATIVE;
    if (
      deal.marketId != null &&
      String(deal.marketId) !== String(config.marketId)
    )
      return NATIVE;
    if (
      deal.blockVisibility === "selected-products" &&
      Array.isArray(deal.selectedProductIds) &&
      !deal.selectedProductIds.some((id) => String(id) === productId)
    )
      return NATIVE;

    const bar = deal.dealBars.find(
      (candidate) => !candidate.hideBehindScratchOff,
    );
    if (!bar) return NATIVE;
    if (
      bar.dealBarType !== "quantity-break" ||
      bar.sellingPlanEnabled ||
      bar.showAsSoldOutEnabled
    )
      return FALLBACK;
    const quantity = Number(bar.quantity);
    if (!Number.isInteger(quantity) || quantity <= 0) return FALLBACK;
    const type = bar.discount?.type ?? bar.discountType;
    if (type === "default" || (type == null && bar.discount == null)) {
      return { kind: "variant", currency };
    }
    if (type !== "specific") return FALLBACK;
    const value = bar.discount?.value ?? bar.discountValue;
    if (
      value == null ||
      value === "" ||
      !Number.isFinite(Number(value)) ||
      Number(value) < 0
    )
      return FALLBACK;

    // Kaching stores a specific price as the TOTAL bundle amount in store
    // currency. Only convert when its configuration identifies this market.
    const rate = Number(config.currencyRate);
    if (!Number.isFinite(rate) || rate <= 0) return FALLBACK;
    if (deal.currency != null && deal.currency !== "USD") return FALLBACK;
    return {
      kind: "offer",
      cents: Math.round((Number(value) * 100 * rate) / quantity),
      currency,
    };
  } catch {
    return FALLBACK;
  }
}

/** @type {Map<string, {request: Promise<ReturnType<typeof readEdenDeal>>, result?: ReturnType<typeof readEdenDeal>}>} */
const deals = new Map();

/**
 * @param {string} productId
 * @param {string} productUrl
 * @param {string} currency
 */
function dealKey(productId, productUrl, currency) {
  return JSON.stringify([productId, productUrl, currency]);
}

/**
 * One bounded request per product, URL and currency, shared by price and badge.
 * @param {string} productId
 * @param {string} productUrl
 * @param {string} currency
 */
function loadDeal(productId, productUrl, currency) {
  const key = dealKey(productId, productUrl, currency);
  const existing = deals.get(key);
  if (existing) return existing.request;
  const entry = {
    request: (async () => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 5000);
      try {
        const url = new URL(productUrl, window.location.origin);
        if (url.origin !== window.location.origin) return FALLBACK;
        url.search = "";
        url.hash = "";
        const response = await fetch(url, {
          signal: controller.signal,
          credentials: "same-origin",
          headers: { Accept: "text/html" },
        });
        if (!response.ok) return FALLBACK;
        const html = await response.text();
        return readEdenDeal(
          new DOMParser().parseFromString(html, "text/html"),
          currency,
          productId,
        );
      } catch {
        return FALLBACK;
      } finally {
        window.clearTimeout(timeout);
      }
    })(),
    result: undefined,
  };
  entry.request = entry.request.then((result) => (entry.result = result));
  deals.set(key, entry);
  return entry.request;
}

/**
 * Lives inside priceContainer, so both replacement and morph-based variant
 * updates keep the offer. Native markup remains available for PDP/quick add.
 */
class EdenCardPrice extends Component {
  #generation = 0;
  #waiting = "";
  // Section rendering can retain this element without invoking its update
  // callback. Observe its own markup so the cached offer survives that morph.
  #priceObserver = new MutationObserver(() => this.#refresh());

  connectedCallback() {
    super.connectedCallback();
    this.#refresh();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#priceObserver.disconnect();
    this.#generation++;
    this.#waiting = "";
    // The shared request belongs to all cards and has its own five-second
    // abort timer. A detached card drops its subscription, not others' work.
  }

  #refresh() {
    if (
      !this.isConnected ||
      !this.closest(CARD_SELECTOR) ||
      this.closest(QUICK_ADD_SELECTOR)
    )
      return;
    // Ignore our own price/badge writes and discard the external mutation
    // batch now that its final state is available to #apply.
    this.#priceObserver.disconnect();
    const { productId, productUrl, currency } = this.dataset;
    const key = dealKey(productId, productUrl, currency);
    const result = deals.get(key)?.result;
    try {
      this.#apply(result ?? FALLBACK);
    } finally {
      this.#priceObserver.observe(this, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          "data-mode",
          "data-base-price",
          "data-variant-price",
          "data-fallback-price",
          "data-currency",
          "data-currency-code",
          "data-kind",
          "data-product-url",
          "data-product-id",
          "hidden",
          "class",
        ],
      });
    }
    if (result || this.#waiting === key) return;
    if (!productId || !productUrl || !currency) return;
    this.#waiting = key;
    const generation = this.#generation;
    void loadDeal(productId, productUrl, currency).then(() => {
      if (
        generation !== this.#generation ||
        !this.isConnected ||
        this.#waiting !== key
      )
        return;
      this.#waiting = "";
      this.#refresh();
    });
  }

  /** @param {ReturnType<typeof readEdenDeal>} result */
  #apply(result) {
    const currency = this.dataset.currency;
    if (!currency) return;
    if (
      result.kind === "native" ||
      (result.currency && result.currency !== currency)
    ) {
      this.dataset.mode = "native";
      return;
    }
    if (result.kind === "fallback" && currency !== "USD") {
      this.dataset.mode = "native";
      return;
    }

    const cents =
      result.kind === "offer"
        ? result.cents
        : Number(
            result.kind === "variant"
              ? this.dataset.variantPrice
              : this.dataset.fallbackPrice,
          );
    const base = Number(this.dataset.basePrice);
    if (
      typeof cents !== "number" ||
      !Number.isFinite(cents) ||
      !Number.isFinite(base)
    )
      return;
    const onSale = base > cents;
    this.dataset.mode = "offer";
    const offer = this.querySelector("[data-eden-offer]");
    if (!offer) return;

    const compare = offer.querySelector("[data-offer-compare]");
    if (compare && result.kind !== "fallback") {
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
      }).format(base / 100);
      compare.textContent =
        this.dataset.currencyCode === "true"
          ? `${formatted} ${currency}`
          : formatted;
    }
    const price = offer.querySelector("[data-offer-price]");
    if (price) {
      // Preserve Shopify's exact initial money formatting for the fallback.
      if (result.kind !== "fallback") {
        const formatted = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency,
        }).format(cents / 100);
        price.textContent =
          this.dataset.currencyCode === "true"
            ? `${formatted} ${currency}`
            : formatted;
      }
      price.classList.toggle("price--on-sale", onSale);
    }
    for (const node of offer.querySelectorAll(
      "[data-offer-compare-group], [data-offer-sale-label], [data-offer-percent]",
    )) {
      node.toggleAttribute("hidden", !onSale);
    }
    const percent = offer.querySelector("[data-offer-percent]");
    if (percent)
      percent.textContent = `${onSale ? Math.round(((base - cents) * 100) / base) : 0}% OFF`;
    const badge = offer.querySelector("[data-offer-badge]");
    if (badge)
      badge.toggleAttribute(
        "hidden",
        !onSale && !badge.classList.contains("product-badge--flash-sale"),
      );
  }
}

if (!customElements.get("fts-eden-card-price")) {
  customElements.define("fts-eden-card-price", EdenCardPrice);
}
